import { Button, Form, FormContext } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import { range } from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectOptions } from 'src/js/component/base/form/form-select';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { FormBuilder, FormFieldType, FormLayout } from 'src/js/component/form-builder';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { triggerEvent, watchEvent } from 'src/js/events';
import { enumValues } from 'src/js/helper/enum-helper';
import { useStores } from 'src/js/hook/use-stores';
import { EssenceState, EssenceType } from 'src/js/pages/streaming/essence/essence-models';
import essenceModels from 'src/js/pages/streaming/essence/essence-store';
import videoInputs, { fetchVideoInputs, videoInputStore } from 'src/js/pages/streaming/video-input/video-input-store';
import { RebootNeeded, RebootNeededRef } from 'src/js/reboot/reboot-needed';
import { useWatchObject } from 'src/js/store/use-watch';
import { PRODUCT, constant } from 'src/js/constant';
import { changes } from 'src/js/util/formik-helper';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { submitAndValidate } from 'src/js/validator/validator';

import {
  ASPECT_RATIO,
  ASPECT_RATIO_WITHOUT_WSS,
  CHROMA_SUBSAMPLING,
  COUNTING_MODE,
  ColorSpaces,
  DYNAMIC_RANGE_MODE,
  DYNAMIC_RANGE_MODE_8BIT,
  ENCODING_PROFILE,
  ENCODING_PROFILE_H264,
  ENCODING_PROFILE_HEVC,
  GOP_STRUCTURE,
  GOP_STRUCTURE_IP,
  RATE_CONTROL,
  RESIZE_MODE,
  ResolutionsMatrix,
  TIMECODE_SOURCE,
  VIDEO_CODEC,
  VIDIN_CONNECTOR,
  VideoEncoderViewModel,
  formatResolution,
  isInterlaced,
  isProgressive,
  videoEncoderValidationSchema,
} from './video-encoder-models';
import videoEncoders, {
  fetchVideoEncoder,
  resolutions,
  resolutionsHdmi,
  updateVideoEncoder,
} from './video-encoder-store';
import { FormSlider } from 'src/js/component/base/form/form-slider';
import { useMutable } from 'src/js/hook/use-mutable';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { NotificationVariant } from 'src/js/notification/notification';
import streams from '../stream/stream-store';
import { StreamEncapsulation, StreamShaping, StreamState } from '../stream/stream-models';

interface Props {
  model: VideoEncoderViewModel;
  onCancel: VoidFunction;
}

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const VIDEO_INPUT_CACHE_DURATION_MS = 300000;

export const VideoEncoderEdit: React.FunctionComponent<Props> = ({ model, onCancel }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const formRef = useRef<FormikProps<VideoEncoderViewModel>>();

  const rebootNeededRef = useRef<RebootNeededRef>(null);
  const paramsThatNeedsReboot: (keyof VideoEncoderViewModel)[] = ['hdcp'];
  const edidParam: keyof VideoEncoderViewModel = 'preferredResolution';

  useEffect(() => {
    // fetch individual encoder ID to get video input params
    fetchVideoEncoder(model.id, t);
  }, []);

  // setting a state here prevents live update from modifying the edit form
  const [settings, setSettings] = useState<VideoEncoderViewModel>(model);
  const [resolution, setResolution] = useState(model.resolution);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);

  const [resolutionResizeModified, setResolutionResizeModified] = useMutable(false); // NKE-3011

  const [aspectRatioWasTweaked, setAspectRatioWasTweaked] = useMutable(false); // WALU-246 issue from the comment section
  const [gopStructureWasTweaked, setGopStructureWasTweaked] = useMutable<number | false>(false); // NKE-3641

  const [fetchVideoInputDone, setFetchVideoInputDone] = useMutable(false);
  const [fetchVideoEncDone, setFetchVideoEncDone] = useMutable(false);
  const [loading, setLoading] = useState(true);

  const associatedStreams = streams.filter((s) => s.video && s.video.length > 0 && s.video[0].id === model.id);
  //console.log('associatedStreams', associatedStreams);
  const anyStreamWithNetworkAdaptive =
    associatedStreams.find((s) => s.adaptive == true && s.state !== StreamState.STOPPED) !== undefined;
  const anyStartedWhipStream =
    associatedStreams.find((s) => s.encapsulation === StreamEncapsulation.WHIP && s.state !== StreamState.STOPPED) !==
    undefined;
  const anyStartedStreamWithCBR =
    associatedStreams.find((s) => s.shaping === StreamShaping.CBR && s.state !== StreamState.STOPPED) !== undefined;
  const anyStartedStreamWithCVBR =
    associatedStreams.find((s) => s.shaping === StreamShaping.CVBR && s.state !== StreamState.STOPPED) !== undefined;
  //console.log('anyStreamWithNetworkAdaptive', anyStreamWithNetworkAdaptive);
  useEffect(() => {
    // only update selected ones to not reset the form while the user is editing
    // console.warn('detected input interface CHANGE', model.interface, model.stats.inputFormat);
    if (constant.product === PRODUCT.FX) {
      const newSettings = {
        ...settings,
        inputInterface: model.inputInterface,
        inputFormat: model.inputFormat,
        colorSpaceActual: model.colorSpaceActual,
        stats: model.stats,
      };
      setSettings(newSettings);
      formRef.current?.resetForm({ values: newSettings });
    }
    // if we are on FX, there is only one edid. If one was fetched less than 30 seconds ago, do not refetch it.
    if (
      constant.product === PRODUCT.FX &&
      Date.now() - videoInputStore.updated.getTime() < VIDEO_INPUT_CACHE_DURATION_MS
    ) {
      // console.log('vidin fetch is recent enough')
      setFetchVideoInputDone(true);
      return;
    } else {
      // console.log('vidin not recent enough, will re-fetch')
    }
    fetchVideoInputs();
  }, [model.inputInterface, model.stats.inputFormat]); // inputFormat detection is to adjust the resolution downscale matrix

  const [resetOnNextReset, setResetOnNextReset] = useMutable(false);
  watchEvent('preset-load', () => {
    setResetOnNextReset(true);
  });

  useWatchObject(videoEncoders, (_videncs) => {
    if (fetchVideoEncDone() === false) {
      setFetchVideoEncDone(true);
      setResetOnNextReset(false);
      const videoEncoder = videoEncoders.find((v) => model.id == v.id);
      if (videoEncoder) {
        setSettings(videoEncoder);
        formRef.current?.resetForm({ values: videoEncoder });
        // console.log('resetting form with inputInterface', videoEncoder?.inputInterface);
      }
      if (fetchVideoInputDone() && fetchVideoEncDone()) {
        // all init edit form
        setLoading(false);
      }
    } else if (resetOnNextReset()) {
      const videoEncoder = videoEncoders.find((v) => model.id == v.id);
      if (videoEncoder) {
        setSettings(videoEncoder);
        formRef.current?.resetForm({ values: videoEncoder });
      }
      setResetOnNextReset(false);
    }
  });
  useWatchObject(videoInputs, (_vidins) => {
    if (fetchVideoInputDone() === false) {
      setFetchVideoInputDone(true);
      if (fetchVideoInputDone() && fetchVideoEncDone()) {
        // all init edit form
        setLoading(false);
      }
    }
  });

  const [cancelSpinner, setCancelSpinner] = useMutable(false);
  const f_showSpinner = (body: string) => {
    if (cancelSpinner() === false) {
      triggerEvent('show-wait-dialog', { title: t('videnc.edidNotice.title'), body: body });
    }
    setCancelSpinner(false);
  };

  const handleSubmit = (values: any) => {
    setButtonState('pending');
    const formikProps = formRef.current;
    const paramsNeedingReboot = paramsThatNeedsReboot.filter((value: keyof VideoEncoderViewModel) => {
      return formikProps.initialValues[value] != values[value];
    });
    const rebootNeeded = paramsNeedingReboot.length > 0;

    const edidChanged = [edidParam].filter((value: keyof VideoEncoderViewModel) => {
      if (formRef.current) {
        // console.log('edidChanged? ', formikProps.initialValues[value], values[value]);
        return formikProps.initialValues[value] != values[value];
      }
      return false;
    });
    const edidNewValue = values[edidParam];
    const edidModalBody =
      edidNewValue === 'default'
        ? t('videnc.edidNotice.body')
        : t('videnc.edidNotice.bodyNonDefault', { resolution: edidNewValue });
    const timerId = edidChanged?.length > 0 && setTimeout(() => f_showSpinner(edidModalBody), 100);
    return updateVideoEncoder(values, t).then((res) => {
      clearTimeout(timerId);
      setCancelSpinner(false);
      triggerEvent('close-wait-dialog');
      if (res.ok) {
        const videoEncoder = videoEncoders.find((e) => e.id === model.id);
        setResolutionResizeModified(false); // reset tweak NKE-3011
        setSettings(videoEncoder); // update edit view with newest model
        formikProps.resetForm({ values: videoEncoder });
        triggerEvent('preset-update');
        if (rebootNeeded || res.data?.message?.includes('EDID')) {
          rebootNeededRef.current.show();
          if (res.data?.message?.includes('EDID')) {
            createAndDispatchNotification(res.data?.message, NotificationVariant.WARNING, t);
          }
        }
      }
      setButtonState(undefined);
      return res;
    });
  };

  let previousChroma = settings.chromaSubSampling;
  const onChangeChromaSubSampling = (e: any) => {
    const formikProps = formRef.current;
    const chroma = e.target.value;
    if (previousChroma == CHROMA_SUBSAMPLING.CHROMA_420_8BIT || previousChroma == CHROMA_SUBSAMPLING.CHROMA_422_8BIT) {
      // 8-bit
      if (chroma == CHROMA_SUBSAMPLING.CHROMA_420_10BIT || chroma == CHROMA_SUBSAMPLING.CHROMA_422_10BIT) {
        // 10-bit
        // switched from 8-bit to 10-bit, will pre-fill dropdown with Automatic
        // In the past we switched to Automatic when the user sets 10-bit (NKE-1052)
        // Since 1.1 now that we show the dynamic range dropdown all the time, we dont tweak it
        // if (formikProps && systemInfo.hasHDR) {
        //  formikProps.setFieldValue('dynamicRangeMode', DYNAMIC_RANGE_MODE.AUTOMATIC, true);
        //  formikProps.setFieldTouched('dynamicRangeMode', true);
        //}
      }
    } else if (
      previousChroma == CHROMA_SUBSAMPLING.CHROMA_420_10BIT ||
      previousChroma == CHROMA_SUBSAMPLING.CHROMA_422_10BIT
    ) {
      // 10-bit
      if (chroma == CHROMA_SUBSAMPLING.CHROMA_420_8BIT || chroma == CHROMA_SUBSAMPLING.CHROMA_422_8BIT) {
        // 8-bit
        // switched from 10-bit to 8-bit, will pre-fill dropdown with Automatic
        if (formikProps && systemInfo.hasHDR && formikProps.values.dynamicRangeMode > DYNAMIC_RANGE_MODE.AUTOMATIC) {
          formikProps.setFieldValue('dynamicRangeMode', DYNAMIC_RANGE_MODE.OFF, true);
          formikProps.setFieldTouched('dynamicRangeMode', true);
        }
      }
    }
    previousChroma = chroma;
  };

  // second parameter encodingProfile is the newly selected one
  const fixChromaDropdown = (chroma: CHROMA_SUBSAMPLING, encodingProfile: ENCODING_PROFILE) => {
    const formikProps = formRef.current;
    if (encodingProfile === ENCODING_PROFILE.HIGH_10 || encodingProfile === ENCODING_PROFILE.MAIN_10) {
      // High 10, Main 10
      if (chroma === CHROMA_SUBSAMPLING.CHROMA_422_8BIT) {
        formikProps.setFieldValue('chromaSubSampling', CHROMA_SUBSAMPLING.CHROMA_420_8BIT);
      } else if (chroma === CHROMA_SUBSAMPLING.CHROMA_422_10BIT) {
        formikProps.setFieldValue('chromaSubSampling', CHROMA_SUBSAMPLING.CHROMA_420_10BIT);
      }
    } else if (encodingProfile === ENCODING_PROFILE.HIGH_422 || encodingProfile === ENCODING_PROFILE.MAIN_422) {
      // High 4:2:2, Main 4:2:2 10
      // nothing
    } else {
      // revert to 8-bit if needed
      if (chroma !== CHROMA_SUBSAMPLING.CHROMA_420_8BIT) {
        formikProps.setFieldValue('chromaSubSampling', CHROMA_SUBSAMPLING.CHROMA_420_8BIT);
      }
      if (formikProps && systemInfo.hasHDR && formikProps.values.dynamicRangeMode > DYNAMIC_RANGE_MODE.AUTOMATIC) {
        formikProps.setFieldValue('dynamicRangeMode', DYNAMIC_RANGE_MODE.OFF, true);
        formikProps.setFieldTouched('dynamicRangeMode', true);
      }
    }
  };

  const fixEncodingProfileOfOtherCodec = (codecAlgorithm: VIDEO_CODEC, encodingProfile: ENCODING_PROFILE) => {
    const formikProps = formRef.current;
    let newEncodingProfile = undefined;
    if (codecAlgorithm === VIDEO_CODEC.H265) {
      // adjust to HEVC corresponding value
      if (encodingProfile === ENCODING_PROFILE.BASELINE) {
        newEncodingProfile = ENCODING_PROFILE.MAIN;
      } else if (encodingProfile === ENCODING_PROFILE.MAIN) {
        newEncodingProfile = ENCODING_PROFILE.MAIN;
      } else if (encodingProfile === ENCODING_PROFILE.HIGH) {
        newEncodingProfile = ENCODING_PROFILE.MAIN;
      } else if (encodingProfile === ENCODING_PROFILE.HIGH_10) {
        newEncodingProfile = ENCODING_PROFILE.MAIN_10;
      } else if (encodingProfile === ENCODING_PROFILE.HIGH_422) {
        newEncodingProfile = ENCODING_PROFILE.MAIN_422;
      }

      if (newEncodingProfile) {
        formikProps.setFieldValue('encodingProfile', newEncodingProfile);
      }
    } else {
      // adjust to H264 corresponding value
      if (encodingProfile === ENCODING_PROFILE.MAIN) {
        // no changes
      } else if (encodingProfile === ENCODING_PROFILE.MAIN_10) {
        newEncodingProfile = ENCODING_PROFILE.HIGH_10;
      } else if (encodingProfile === ENCODING_PROFILE.MAIN_422) {
        newEncodingProfile = ENCODING_PROFILE.HIGH_422;
      }

      if (newEncodingProfile) {
        formikProps.setFieldValue('encodingProfile', newEncodingProfile);
      }
    }
  };

  // frameRate helpers BEGINs
  const isFrameRateDoubled = (selectedResolutionInteger: string | number) => {
    if (
      selectedResolutionInteger !== 0 &&
      selectedResolutionInteger !== '0' &&
      isInterlaced(settings.stats.inputFormatU64) &&
      isProgressive(selectedResolutionInteger)
    ) {
      // console.log('isFrameRateDoubled? yes');
      return true;
    }
    // console.log('isFrameRateDoubled? no');
    return false;
  };

  const getCalculatedFrameRate = () => {
    const stats = settings.stats;
    let frameRate = stats.inputFormatFrameRate || 60;
    if (frameRate % 10 == 9) {
      // 29 => 30, because 29.97
      frameRate++;
    }
    // console.log('getCalculatedFrameRate', frameRate);
    return frameRate;
  };
  // frameRate helpers ENDs

  // resolution dropdown BEGINs

  // exclusion can have things like "9223372038331318273" to exclude 352x288i for example. Also supports "interlaced" for blanket removal of all interlaced resolutions
  // if the input resolution is interlaced, allow both progressive and interlaced, but block cropping if progressive is selected (NKE-555).
  // if the input resolution is progressive, and we are cropping, only allow progressive
  const calculateExclusions = (formikValues: VideoEncoderViewModel) => {
    const exclusions: string[] = [];
    if (formikValues) {
      if (
        formikValues.codecAlgorithm == VIDEO_CODEC.H264 ||
        (formikValues.cropping == RESIZE_MODE.CROP && settings.stats.inputFormatIsProgressive)
      ) {
        exclusions.push('interlaced');
      } else if (constant.product === PRODUCT.X1 && settings.id === 1 && settings.stats.inputFormatIsInterlaced) {
        // on MX1 only enc 0 can deinterlace (MX1-490 and MX1-491)
        exclusions.push('progressive');
      } else if (formikValues.cropping == RESIZE_MODE.CROP && settings.stats.inputFormatIsInterlaced) {
        // NKE-555
      }
    }
    if (constant.product === PRODUCT.X1) {
      exclusions.push('9223372052962009088');
    }
    return exclusions;
  };

  //Checking if the resolutions options if the SDI options or the HDMI/DisplayPort options
  const getResolutionsMatrix = (inputInterface: VIDIN_CONNECTOR): ResolutionsMatrix => {
    if (inputInterface === undefined) {
      return {};
    }
    return inputInterface === VIDIN_CONNECTOR.SDI ? resolutions : resolutionsHdmi;
  };

  //const resolutionsMatrix: ResolutionsMatrix = getResolutionsMatrix(formRef.current?.values.inputInterface);
  // never used const [resolutionOptionsWereCalculatedOn, setResolutionOptionsWereCalculatedOn] = useMutable(null);

  const getResolutions = (formikValues: VideoEncoderViewModel) => {
    const resolutionsMatrix: ResolutionsMatrix = getResolutionsMatrix(formRef.current?.values.inputInterface);
    // console.log('getResolutions, inputInterface', resolutionsMatrix, formikValues?.inputInterface, formRef.current?.values.inputInterface);
    let resolutionOptions: SelectOptions = [];
    const exclusions = calculateExclusions(formikValues);
    const frameRate = getCalculatedFrameRate();

    if (!isNilOrEmpty(resolutionsMatrix)) {
      let displayResolutions = resolutionsMatrix.def;
      const inputFormat = formatResolution(settings.stats?.inputFormat); // removes framerate
      if (resolutionsMatrix[inputFormat]) {
        displayResolutions = resolutionsMatrix[inputFormat];
        // setResolutionOptionsWereCalculatedOn(inputFormat);
      } else {
        // setResolutionOptionsWereCalculatedOn('def');
      }
      resolutionOptions = displayResolutions.map((resolution) => {
        if (exclusions.includes(resolution.value)) {
          return undefined;
        }
        if (exclusions.includes('interlaced')) {
          // remove all interlaced
          if (isInterlaced(resolution.value)) {
            return undefined; // exclude this one
          }
        }
        if (exclusions.includes('progressive')) {
          // remove all progressive
          if (isProgressive(resolution.value) && resolution.value !== '0') {
            // 0 means automatic
            return undefined; // exclude this one
          }
        }

        if (resolution.frameRates) {
          if (!resolution.frameRates.includes(frameRate)) {
            // Only show if current resolution is in array of valid resolutions
            return undefined;
          }
        }

        return {
          value: resolution.value,
          label: resolution.name,
        };
      });

      resolutionOptions = resolutionOptions.filter(Boolean);
    } else {
      // console.warn('resolution matrix not yet available')
    }
    // console.log('resolutionOptions', resolutionOptions);
    return resolutionOptions;
  };

  // using Memo would be a good idea but we would need to add the remaining dependant inputs and unit test it
  /*
  let resolutionMatrixInput = 'def';
  if (!isNilOrEmpty(resolutionsMatrix)) {
    const inputFormat = formatResolution(settings.stats?.inputFormat); // removes framerate
    if (resolutionsMatrix[inputFormat]) {
      resolutionMatrixInput = inputFormat;
    }
  }
  const [resolutionMatrixInputSaved, setResolutionMatrixInputSaved] = useState(resolutionMatrixInput);
  if (resolutionMatrixInputSaved !== resolutionMatrixInput) {
    // recalculate
    setResolutionMatrixInputSaved(resolutionMatrixInput);
  }

  //let resolutionOptions = useMemo(() => getResolutions(formRef.current?.values), [formRef.current?.values, resolutionMatrixInput]);
  // */

  let resolutionOptions = getResolutions(formRef.current?.values);

  // console.log('resolutionOptions', resolutionOptions);
  let lastCodecAlgorithm = formRef.current?.values?.codecAlgorithm;
  let scaleModeUsedForDropdown = formRef.current?.values?.cropping;
  // resolution dropdown ENDs

  let st2110VideoOptions: SelectOptions = [];
  let st2110DataOptions: SelectOptions = [];
  if (systemInfoCapabilities.is2110()) {
    st2110VideoOptions = range(0, systemInfo.videoInputs).map((input: number) => ({
      value: input,
      label: (input + 1).toString(),
      disabled: true,
    }));

    essenceModels
      .filter((e) => e.type === EssenceType.video && e.state === EssenceState.RUNNING)
      .map((input) => {
        const option = st2110VideoOptions.find((option: any) => option.value == input.inputChannel);
        if (option) {
          option.label = !isNilOrEmpty(input.name)
            ? `${option.label} (${input.name})`
            : `${option.label} (${input.address}:${input.port})`;
          option.disabled = false;
        }
      });

    st2110DataOptions = range(0, systemInfo.videoInputs).map((input) => ({
      value: input,
      label: (input + 1).toString(),
      disabled: true,
    }));
    essenceModels
      .filter((e) => e.type === EssenceType.data && e.state === EssenceState.RUNNING)
      .map((input) => {
        const option = st2110DataOptions.find((option: any) => option.value == input.inputChannel);
        if (option) {
          option.label = !isNilOrEmpty(input.name)
            ? `${option.label} (${input.name})`
            : `${option.label} (${input.address}:${input.port})`;
          option.disabled = false;
        }
      });
  }

  const is8bit = (chromaSubSampling: CHROMA_SUBSAMPLING) => {
    if (
      chromaSubSampling === CHROMA_SUBSAMPLING.CHROMA_420_8BIT ||
      chromaSubSampling === CHROMA_SUBSAMPLING.CHROMA_422_8BIT
    ) {
      return true;
    }
    return false;
  };

  const matrix = getResolutionsMatrix(settings.inputInterface);
  // console.log('loading, matrix', loading, matrix, resolutions);
  if (isNilOrEmpty(matrix)) {
    return undefined; // otherwise the form might load with an empty resolution dropdown
  }

  return (
    <>
      <Form
        initialValues={settings}
        defaultValidation={true}
        handleSubmit={handleSubmit}
        className="hai-mt-n8"
        restValidationProps={{
          enableReinitialize: false, // "false" requires manual resetForm() above. We can then do partial update of the form while it's opened.
          validationSchema: videoEncoderValidationSchema(t),
          innerRef: formRef,
        }}
      >
        <FormContext.Consumer>
          {(formContext: FormikProps<VideoEncoderViewModel>) => {
            const values = formContext.values;
            const videoInput = videoInputs.find((vi) => vi.id === values.interface);
            if (lastCodecAlgorithm !== values.codecAlgorithm || scaleModeUsedForDropdown != values.cropping) {
              lastCodecAlgorithm = values.codecAlgorithm;
              scaleModeUsedForDropdown = values.cropping;
              resolutionOptions = getResolutions(values);
            }
            const resolutionInDropdown = resolutionOptions.find((r) => r.value === values.resolution);
            if (
              resolutionInDropdown === undefined &&
              resolutionOptions &&
              resolutionOptions.length > 0 &&
              values.resolution !== '0'
            ) {
              values.resolution = resolutionOptions[0].value as string; // pick first (Automatic)
            }
            const inputIsInterlaced = isInterlaced(settings.stats?.inputFormatU64);
            const outputIsProgressive = isProgressive(values.resolution);

            let maxFrameRate = getCalculatedFrameRate();
            if (isFrameRateDoubled(resolution)) {
              maxFrameRate = getCalculatedFrameRate() * 2;
            }
            if (values.pictureRate > 0 && maxFrameRate > 0 && values.pictureRate > maxFrameRate) {
              // console.warn('pictureRate out of range', values.pictureRate, maxFrameRate);
              values.pictureRate = 0; // automatic
            }

            const showDynamicRange = systemInfo.hasHDR; // WALU-505 always show

            // Handle WSS/AFD option
            if (
              values.aspectRatio === ASPECT_RATIO.ASPECT_RATIO_WSS_AFD &&
              constant.product === PRODUCT.FX &&
              (values.inputInterface === VIDIN_CONNECTOR.HDMI || values.inputInterface === VIDIN_CONNECTOR.DISPLAYPORT)
            ) {
              formContext.setFieldValue('aspectRatio', ASPECT_RATIO.AUTOMATIC);
              setAspectRatioWasTweaked(true);
            } else if (
              aspectRatioWasTweaked() === true &&
              values.aspectRatio === ASPECT_RATIO.AUTOMATIC &&
              constant.product === PRODUCT.FX &&
              values.inputInterface === VIDIN_CONNECTOR.SDI
            ) {
              formContext.setFieldValue('aspectRatio', ASPECT_RATIO.ASPECT_RATIO_WSS_AFD);
              setAspectRatioWasTweaked(false);
            }

            if (values.encodingProfile === ENCODING_PROFILE.BASELINE && values.gopStructure >= GOP_STRUCTURE.IBP) {
              setGopStructureWasTweaked(values.gopStructure);
              formContext.setFieldValue('gopStructure', GOP_STRUCTURE.IP);
            } else if (
              gopStructureWasTweaked() !== false &&
              values.encodingProfile !== ENCODING_PROFILE.BASELINE &&
              values.gopStructure === GOP_STRUCTURE.IP
            ) {
              // user turned it back to non-Baseline profile, restore previous GOP structure that was auto-changed
              formContext.setFieldValue('gopStructure', gopStructureWasTweaked());
              setGopStructureWasTweaked(false);
            }

            let inputFormatAddon = '';
            if (!systemInfoCapabilities.is2110()) {
              if (model.videoInputState === '2SI ACTIVE') {
                inputFormatAddon = ' (2SI)';
              } else if (model.videoInputState === '2SI FAILED') {
                inputFormatAddon = ' (2SI FAILED)';
              } else if (model.videoInputState === 'SQD ACTIVE') {
                inputFormatAddon = ' (SQD)';
              } else if (model.videoInputState === 'SQD FAILED') {
                inputFormatAddon = ' (SQD FAILED)';
              }
            }

            let applyButtonEnabled = formContext.dirty;
            if (applyButtonEnabled) {
              const changed = changes(values, formContext.initialValues);
              if (changed.countingMode !== undefined) {
                if (values.timeCode !== TIMECODE_SOURCE.SYSTEM) {
                  delete changed.countingMode; // countingMode is hidden ignore changes
                }
              }
              if (changed.dailyResync !== undefined) {
                if (values.timeCode !== TIMECODE_SOURCE.SYSTEM || values.countingMode !== COUNTING_MODE.SMPTE_12M_1) {
                  delete changed.dailyResync; // dailyResync is hidden ignore changes
                }
              }
              if (changed.maxBitrate !== undefined) {
                if (values.rateControl === RATE_CONTROL.CBR) {
                  delete changed.maxBitrate; // maxBitrate is not show in CBR
                }
              }
              if (changed.dynamicRangeMode !== undefined) {
                if (!showDynamicRange) {
                  delete changed.dynamicRangeMode;
                }
              }
              if (changed.sceneChangeThreshold !== undefined) {
                if (!values.sceneChangeDetection) {
                  delete changed.sceneChangeThreshold; // ignore any change of slider if toggled off
                }
              }

              if (Object.keys(changed).length === 0) {
                applyButtonEnabled = false;
              }
            }

            // console.log('Video Edit Form render resolution:', values.resolution, resolutionOptions)

            return (
              <>
                <div className="mk-mt-panel-name-align">
                  <FormTextfield
                    name="name"
                    mask={model.name !== `Video Encoder ${model.id}`}
                    placeholder={`Video Encoder ${model.id}`}
                    title={t('general.name')}
                    className="max-width"
                  />
                  {!sessionStore.isUser() && !loading && (
                    <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
                      <Button className="hai-mr-6" onClick={onCancel}>
                        {t('general.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => submitAndValidate(formContext, t)}
                        disabled={!applyButtonEnabled}
                        state={buttonState}
                      >
                        {t('general.apply')}
                      </Button>
                    </div>
                  )}
                </div>
                <FormBuilder
                  translationPrefixLabels="videnc.fields"
                  translationPrefixEnums="videnc.enums"
                  layout={isSafari ? FormLayout.THREE_COLUMNS_GRID : FormLayout.THREE_COLUMNS_MASONRY}
                  fields={[
                    {
                      type: FormFieldType.TITLE,
                      label: t('videnc.sections.input'),
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'inputInterface',
                      disabled: systemInfo.hasDisplayPort,
                      options: systemInfo.hasDisplayPort
                        ? [
                            {
                              label: t(`videnc.enums.inputInterface.${VIDIN_CONNECTOR.DISPLAYPORT}`),
                              value: VIDIN_CONNECTOR.DISPLAYPORT,
                            },
                          ]
                        : [
                            {
                              label: t(`videnc.enums.inputInterface.${VIDIN_CONNECTOR.SDI}`),
                              value: VIDIN_CONNECTOR.SDI,
                            },
                            {
                              label: t(`videnc.enums.inputInterface.${VIDIN_CONNECTOR.HDMI}`),
                              value: VIDIN_CONNECTOR.HDMI,
                            },
                          ],
                      when: constant.product === PRODUCT.FX,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'colorSpace',
                      options: ColorSpaces.map((c) => ({
                        label: t(`videnc.enums.colorSpace.${c}`),
                        value: c,
                      })),
                      when:
                        constant.product === PRODUCT.FX &&
                        (values.inputInterface === VIDIN_CONNECTOR.HDMI ||
                          values.inputInterface === VIDIN_CONNECTOR.DISPLAYPORT),
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'preferredResolution',
                      value: videoInput?.preferredResolution,
                      options: videoInput?.availableResolutions?.map((resolution) => ({
                        label: resolution === 'default' ? t('videnc.defaultEdidResolution') : resolution,
                        value: resolution,
                      })),
                      when:
                        videoInput?.availableResolutions?.length > 0 &&
                        constant.product === PRODUCT.FX &&
                        (values.inputInterface === VIDIN_CONNECTOR.HDMI ||
                          values.inputInterface === VIDIN_CONNECTOR.DISPLAYPORT),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'colorSpaceActual',
                      value: t(`videnc.enums.colorSpace.${values.colorSpaceActual}`),
                      when:
                        constant.product === PRODUCT.FX &&
                        (values.inputInterface === VIDIN_CONNECTOR.HDMI ||
                          values.inputInterface === VIDIN_CONNECTOR.DISPLAYPORT),
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'interface',
                      label: t('metadata.settings.input'),
                      options: systemInfoCapabilities.is2110()
                        ? st2110VideoOptions
                        : videoInputs.map((input) => ({
                            value: input.id,
                            label: input.name,
                          })),
                      when: constant.product !== PRODUCT.FX,
                      disabled: videoInputs.length === 1,
                    },
                    {
                      type: FormFieldType.NODE,
                      node: (
                        <div key="videnc-checkboxes" className="mk-form-custom-two-checkbox-fields">
                          <FormBuilder
                            translationPrefixLabels="videnc.fields"
                            translationPrefixEnums="videnc.enums"
                            layout={FormLayout.PLAIN_FIELDS}
                            fields={[
                              {
                                type: FormFieldType.CHECKBOX,
                                name: 'firFilter',
                                when: !systemInfoCapabilities.is2110(),
                              },
                              {
                                type: FormFieldType.CHECKBOX,
                                name: 'squareDivisionCapture',
                                when: systemInfoCapabilities.isSQD(),
                              },
                            ]}
                          />
                        </div>
                      ),
                    },
                    {
                      type: FormFieldType.STATIC_TEXT,
                      name: 'inputFormat',
                      value: `${model.inputFormat}${inputFormatAddon}`,
                    },
                    {
                      name: 'copyProtectedContent',
                      type: FormFieldType.STATIC_TEXT,
                      value: formContext.values.copyProtectedContent ? t('general.yes') : t('general.no'),
                      when: systemInfoCapabilities.isDVI(),
                    },
                    {
                      type: FormFieldType.LINE_BREAK,
                    },
                    {
                      type: FormFieldType.CHECKBOX,
                      name: 'hdcp',
                      when:
                        values.inputInterface === VIDIN_CONNECTOR.HDMI ||
                        values.inputInterface === VIDIN_CONNECTOR.DISPLAYPORT,
                      disabled: !systemInfoCapabilities.hasHDCP(),
                    },
                    {
                      type: FormFieldType.TITLE,
                      label: t('videnc.sections.encoderParameters'),
                      className: 'hai-mb-0',
                    },
                    {
                      type: FormFieldType.COLUMN_START,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'codecAlgorithm',
                      options: enumValues(VIDEO_CODEC).map((value) => ({
                        value: value,
                        label: t(`videnc.enums.codecAlgorithm.${value}`),
                      })),
                      tooltip: anyStartedWhipStream ? t('videnc.tooltips.codecAlgorithmWhip') : undefined,
                      disabled: anyStartedWhipStream,
                      onChange: (e: any) => {
                        fixEncodingProfileOfOtherCodec(e.target.value, values.encodingProfile);
                      },
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'encodingProfile',
                      value: values.encodingProfile, // On a native element we wouldnt need to use this hack but HAIUI doesnt react unless we force the defaultValue.
                      options: enumValues(ENCODING_PROFILE_H264).map((value) => {
                        if (
                          (!systemInfo.yuv10bit && value === ENCODING_PROFILE_H264.HIGH_10) ||
                          (!systemInfo.yuv422 && value === ENCODING_PROFILE_H264.HIGH_422)
                        ) {
                          return undefined;
                        }
                        return {
                          value: value,
                          label: t(`videnc.enums.encodingProfile.${value}`),
                        };
                      }),
                      onChange: (e: any) => {
                        fixChromaDropdown(values.chromaSubSampling, e.target.value);
                      },
                      when: formContext.values.codecAlgorithm == VIDEO_CODEC.H264,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'encodingProfile',
                      value: values.encodingProfile, // On a native element we wouldnt need to use this hack but HAIUI doesnt react unless we force the defaultValue.
                      options: enumValues(ENCODING_PROFILE_HEVC).map((value) => {
                        if (
                          (!systemInfo.yuv10bit && value === ENCODING_PROFILE_HEVC.MAIN_10) ||
                          (!systemInfo.yuv422 && value === ENCODING_PROFILE_HEVC.MAIN_422)
                        ) {
                          return undefined;
                        }
                        return {
                          value: value,
                          label: t(`videnc.enums.encodingProfile.${value}`),
                        };
                      }),
                      onChange: (e: any) => {
                        fixChromaDropdown(values.chromaSubSampling, e.target.value);
                      },
                      when: formContext.values.codecAlgorithm == VIDEO_CODEC.H265,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'chromaSubSampling',
                      value: values.chromaSubSampling, // On a native element we wouldnt need to use this hack but HAIUI doesnt react unless we force the defaultValue.
                      onChange: onChangeChromaSubSampling,
                      options: [
                        {
                          value: CHROMA_SUBSAMPLING.CHROMA_420_8BIT,
                          label: t(`videnc.enums.chromaSubSampling.${CHROMA_SUBSAMPLING.CHROMA_420_8BIT}`),
                        },
                        {
                          value: CHROMA_SUBSAMPLING.CHROMA_420_10BIT,
                          label: t(`videnc.enums.chromaSubSampling.${CHROMA_SUBSAMPLING.CHROMA_420_10BIT}`),
                          when:
                            (values.encodingProfile === ENCODING_PROFILE.HIGH_10 ||
                              values.encodingProfile === ENCODING_PROFILE.MAIN_10 ||
                              values.encodingProfile === ENCODING_PROFILE.HIGH_422 ||
                              values.encodingProfile === ENCODING_PROFILE.MAIN_422) &&
                            systemInfo.yuv10bit,
                        },
                        {
                          value: CHROMA_SUBSAMPLING.CHROMA_422_8BIT,
                          label: t(`videnc.enums.chromaSubSampling.${CHROMA_SUBSAMPLING.CHROMA_422_8BIT}`),
                          when:
                            (values.encodingProfile === ENCODING_PROFILE.HIGH_422 ||
                              values.encodingProfile === ENCODING_PROFILE.MAIN_422) &&
                            systemInfo.yuv422,
                        },
                        {
                          value: CHROMA_SUBSAMPLING.CHROMA_422_10BIT,
                          label: t(`videnc.enums.chromaSubSampling.${CHROMA_SUBSAMPLING.CHROMA_422_10BIT}`),
                          when:
                            (values.encodingProfile === ENCODING_PROFILE.HIGH_422 ||
                              values.encodingProfile === ENCODING_PROFILE.MAIN_422) &&
                            systemInfo.yuv10bit &&
                            systemInfo.yuv422,
                        },
                      ],
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'dynamicRangeMode',
                      options: is8bit(values.chromaSubSampling)
                        ? enumValues(DYNAMIC_RANGE_MODE_8BIT).map((value) => ({
                            value: value,
                            label: t(`videnc.enums.dynamicRangeMode.${value}`),
                          }))
                        : enumValues(DYNAMIC_RANGE_MODE).map((value) => ({
                            value: value,
                            label: t(`videnc.enums.dynamicRangeMode.${value}`),
                          })),
                      when: showDynamicRange,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'rateControl',
                      enum: RATE_CONTROL,
                      tooltip: anyStreamWithNetworkAdaptive ? t('videnc.tooltips.rateControl') : undefined,
                      disabled: anyStreamWithNetworkAdaptive,
                      when: !anyStreamWithNetworkAdaptive,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'rateControlDisabled',
                      enum: RATE_CONTROL,
                      forceValue:
                        values.rateControlAdapted != null
                          ? `${t(`videnc.enums.rateControl.${values.rateControl}`)} (adapted to ${t(
                              `videnc.enums.rateControl.${values.rateControlAdapted}`,
                            )})`
                          : t(`videnc.enums.rateControl.${values.rateControl}`),
                      tooltip: anyStreamWithNetworkAdaptive ? t('videnc.tooltips.rateControl') : undefined,
                      disabled: anyStreamWithNetworkAdaptive,
                      when: anyStreamWithNetworkAdaptive,
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'maxBitrate',
                      placeholder: values.maxBitrate.toString().length === 0 ? t('general.auto') : 'kbps',
                      when: values.rateControl === RATE_CONTROL.CVBR,
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'bitrate',
                      hint: 'kbps',
                      required: true,
                    },
                    /* {
                            type: FormFieldType.LINE_BREAK,
                          },*/
                    {
                      type: FormFieldType.CHECKBOX,
                      name: 'partialFrameSkip',
                      addon: false && values.dynamicPartialImageSkip && (
                        <div className="sideTextContainer">
                          <div className="sideText">{t('videnc.dynamicPartialImageSkip')}</div>
                        </div>
                      ),
                      tooltip: values.dynamicPartialImageSkip
                        ? t('videnc.tooltips.dynamicPartialImageSkip')
                        : anyStartedStreamWithCBR
                        ? t('videnc.tooltips.partialImageSkipEnabledForCBR')
                        : anyStartedStreamWithCVBR
                        ? t('videnc.tooltips.partialImageSkipEnabledForCVBR')
                        : undefined,
                      disabled: values.dynamicPartialImageSkip || anyStartedStreamWithCBR || anyStartedStreamWithCVBR,
                    },
                    {
                      type: FormFieldType.COLUMN_END,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'resolution',
                      options: resolutionOptions,
                      disabled:
                        resolutionOptions?.length <= 1 ||
                        (resolutionOptions?.length === 2 &&
                          resolutionOptions[1]?.value === settings.stats?.inputFormatWithoutFramerateU64),
                      onChange: (e: any) => {
                        setResolution(e.target.value);
                        if (
                          values.cropping === RESIZE_MODE.CROP &&
                          inputIsInterlaced &&
                          isProgressive(e.target.value)
                        ) {
                          formContext.setFieldValue('cropping', RESIZE_MODE.SCALE); // NKE-555
                          setResolutionResizeModified(true);
                        } else if (
                          resolutionResizeModified() &&
                          formContext.initialValues.stats.inputFormatIsInterlaced &&
                          isInterlaced(e.target.value)
                        ) {
                          //reverting automatically set cropping
                          formContext.setFieldValue('cropping', RESIZE_MODE.CROP);
                          setResolutionResizeModified(false);
                        }
                      },
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'cropping',
                      options: enumValues(RESIZE_MODE).map((value) => ({
                        value: value,
                        label: t(`videnc.enums.cropping.${value}`),
                        disabled:
                          (value === RESIZE_MODE.CROP && inputIsInterlaced && outputIsProgressive) ||
                          (value === RESIZE_MODE.SCALE && constant.product === PRODUCT.X1 && model.id > 0), // MX1-490
                      })),
                      when: values.resolution !== '0',
                      onChange: () => {
                        setResolutionResizeModified(false);
                      },
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'pictureRate',
                      options: [{ label: t('general.automatic'), value: 0 }].concat(
                        range(1, getCalculatedFrameRate() + 1)
                          .reverse()
                          .map((i) => ({
                            label: i.toString(),
                            value: i,
                          })),
                      ),
                      when: !isFrameRateDoubled(resolution),
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'pictureRate',
                      options: [{ label: t('general.automatic'), value: 0 }].concat(
                        range(1, getCalculatedFrameRate() * 2 + 1)
                          .reverse()
                          .map((i) => ({
                            label: i.toString(),
                            value: i,
                          })),
                      ),
                      when: isFrameRateDoubled(resolution),
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'gopStructure',
                      enum: values.encodingProfile === ENCODING_PROFILE.BASELINE ? GOP_STRUCTURE_IP : GOP_STRUCTURE,
                      onChange: (_e: any) => {
                        setGopStructureWasTweaked(false);
                      },
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'aspectRatio',
                      enum:
                        (constant.product === PRODUCT.FX && values.inputInterface === VIDIN_CONNECTOR.SDI) ||
                        (constant.product !== PRODUCT.FX && systemInfoCapabilities.isSDI())
                          ? ASPECT_RATIO
                          : ASPECT_RATIO_WITHOUT_WSS,
                      className: 'select-max-height-fit',
                      onChange: (_e: any) => {
                        setAspectRatioWasTweaked(false);
                      },
                    },
                    {
                      type: FormFieldType.CHECKBOX,
                      name: 'intraRefresh',
                      tooltip: anyStartedWhipStream ? t('videnc.tooltips.codecAlgorithmWhip') : undefined,
                      disabled: anyStartedWhipStream,
                    },
                    {
                      type: FormFieldType.COLUMN_START,
                    },
                    {
                      type: FormFieldType.INPUT_NUMBER,
                      name: 'gopSize',
                      required: true,
                      when: !values.intraRefresh && !anyStreamWithNetworkAdaptive,
                      tooltip: anyStreamWithNetworkAdaptive ? t('videnc.tooltips.gopSize') : undefined,
                      disabled: anyStreamWithNetworkAdaptive,
                    },
                    {
                      type: FormFieldType.INPUT_TEXT,
                      name: 'gopSizeDisabled',
                      required: true,
                      when: !values.intraRefresh && anyStreamWithNetworkAdaptive,
                      value:
                        values.gopSizeAdapted != null
                          ? `${values.gopSize} (adapted to ${values.gopSizeAdapted})`
                          : `${values.gopSize}`,
                      tooltip: anyStreamWithNetworkAdaptive ? t('videnc.tooltips.gopSize') : undefined,
                      disabled: anyStreamWithNetworkAdaptive,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'slices',
                      options: range(1, 12).map((i) => ({
                        label: i.toString(),
                        value: i,
                      })),
                      when: values.gopStructure < GOP_STRUCTURE.IBP,
                    },
                    {
                      type: FormFieldType.COLUMN_END,
                    },
                    {
                      type: FormFieldType.COLUMN_START,
                    },
                    {
                      type: FormFieldType.CHECKBOX,
                      name: 'closedCaption',
                      when: values.inputInterface === VIDIN_CONNECTOR.SDI,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'timeCode',
                      options: enumValues(TIMECODE_SOURCE).map((value) => {
                        return {
                          value: value,
                          label: t(`videnc.enums.timeCode.${value}`),
                          when: value !== TIMECODE_SOURCE.VIDEO || values.inputInterface === VIDIN_CONNECTOR.SDI,
                        };
                      }),
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'countingMode',
                      enum: COUNTING_MODE,
                      when: values.timeCode === TIMECODE_SOURCE.SYSTEM,
                    },
                    {
                      type: FormFieldType.CHECKBOX,
                      name: 'dailyResync',
                      when:
                        values.timeCode === TIMECODE_SOURCE.SYSTEM && values.countingMode === COUNTING_MODE.SMPTE_12M_1,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'resyncHour',
                      options: range(0, 24).map((i) => ({
                        label: t(`videnc.enums.resyncHour.${i}`),
                        value: i,
                      })),
                      disabled: !values.dailyResync,
                      when:
                        values.timeCode === TIMECODE_SOURCE.SYSTEM && values.countingMode === COUNTING_MODE.SMPTE_12M_1,
                    },
                    {
                      type: FormFieldType.COLUMN_END,
                    },
                    {
                      type: FormFieldType.DROPDOWN,
                      name: 'dataInput',
                      when: systemInfoCapabilities.is2110(),
                      options: st2110DataOptions,
                    },
                    {
                      type: FormFieldType.CHECKBOX,
                      name: 'sceneChangeDetection',
                      onChange: (e: any) => {
                        const checked = e.target.checked;
                        formContext.setFieldValue('sceneChangeDetection', checked);
                      },
                    },
                    {
                      type: FormFieldType.NODE,
                      name: 'sceneChangeThreshold',
                      node: (
                        <>
                          {' '}
                          {/* If we later want to use HaiUI directly, but requires more logic for Formik 
                          <Slider
                            name='sceneChangeThreshold'
                            min="1"
                            withValueInput
                            showRange={{ left: '1%', right: '100%' }}
                            defaultValue="50"
                            label={t('videnc.sceneChangeThreshold')}
                          />*/}
                          <FormSlider
                            name="sceneChangeThreshold"
                            min="1"
                            withValueInput
                            valueInputStyle={{ width: '50px' }}
                            showRange={{ left: '1%', right: '100%' }}
                            title={t('videnc.sceneChangeThreshold')}
                            className={!values.sceneChangeDetection && 'visibilityHidden'}
                          />
                        </>
                      ),
                    },
                  ].filter((f) =>
                    isSafari && (f.type === FormFieldType.COLUMN_START || f.type === FormFieldType.COLUMN_END)
                      ? false
                      : true,
                  )} // remove columns when Safari
                />
              </>
            );
          }}
        </FormContext.Consumer>
      </Form>

      <RebootNeeded ref={rebootNeededRef} />
    </>
  );
};
